import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { usePolicyCommands } from "@devices/components/Commands/hooks";
import { MdmPolicyCommand } from "@devices/types";
import { DragDropContext, Droppable, DroppableProvided, DropResult } from "@hello-pangea/dnd";
import { TableBody, TableRow, Theme } from "@mui/material";
import { MinimalTable, MinimalTableHeader, MinimalTableHeaderCell } from "@shared/table/MinimalTableComponents";
import { PaneElement } from "@sidePanel/shared/PaneElement";

import PolicyCommandItem from "./PolicyCommandItem";

const useStyles = makeStyles()((theme: Theme) => ({
    disabledTable: {
        backgroundColor: theme.palette.emteria.background.paperDeep,
    },
}));

interface PolicyCommandsTableProps {
    groupId: number;
    policyCommands: MdmPolicyCommand[];
}

const PolicyCommandsTable = ({ groupId, policyCommands }: PolicyCommandsTableProps) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["sidepanels"]);

    const {
        updatePolicyCommands,
        loadingState: { updatePolicyLoading },
    } = usePolicyCommands();

    const handleDragEnd = async (result: DropResult) => {
        // Dropped outside of the list
        if (!result.destination) {
            return;
        }

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        // No change in order
        if (sourceIndex === destinationIndex) {
            return;
        }

        const updatedPolicyCommands = [...policyCommands];

        // Reorder the array based on the drag-and-drop result
        const [movedPolicyCommand] = updatedPolicyCommands.splice(sourceIndex, 1);
        updatedPolicyCommands.splice(destinationIndex, 0, movedPolicyCommand);

        await updatePolicyCommands({ groupId, policy: JSON.stringify(updatedPolicyCommands) });
    };

    return (
        <PaneElement.TableLayout contentDeps={[policyCommands.length]}>
            <MinimalTable stickyHeader>
                <MinimalTableHeader>
                    <TableRow>
                        <MinimalTableHeaderCell width={50} />
                        <MinimalTableHeaderCell align="center">
                            {t("sidepanels:groupPanel.table.header.index")}
                        </MinimalTableHeaderCell>
                        <MinimalTableHeaderCell align="left">
                            {t("sidepanels:groupPanel.table.header.command")}
                        </MinimalTableHeaderCell>
                        <MinimalTableHeaderCell align="right" />
                    </TableRow>
                </MinimalTableHeader>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="droppable" direction="vertical" isDropDisabled={updatePolicyLoading}>
                        {(droppableProvided: DroppableProvided) => (
                            <TableBody
                                className={updatePolicyLoading ? classes.disabledTable : ""}
                                ref={droppableProvided.innerRef}
                                {...droppableProvided.droppableProps}
                            >
                                {policyCommands.map((c, index) => (
                                    <PolicyCommandItem
                                        index={index}
                                        policyCommands={policyCommands}
                                        policyCommand={c}
                                        key={index}
                                        groupId={groupId}
                                    />
                                ))}
                                {droppableProvided.placeholder}
                            </TableBody>
                        )}
                    </Droppable>
                </DragDropContext>
            </MinimalTable>
        </PaneElement.TableLayout>
    );
};

export default PolicyCommandsTable;
